import dayjs from "dayjs";
import { useMemo } from "react";

import { LineChart } from "@design-system/LineChart/LineChart";

import { useResourceGroupEmployeeUsageLineChartQuery } from "./ResourceGroupEmployeeUsageLineChart.g";

type Props = {
  clientApplicationId: string;
  daysToShow?: number;
  startDate?: Date;
  endDate?: Date;
};

export const ResourceGroupEmployeeUsageLineChart = ({
  clientApplicationId,
  daysToShow = 30,
  startDate,
  endDate,
}: Props) => {
  const { dateFrom, dateTo } = useMemo(() => {
    if (startDate && endDate) {
      return {
        dateFrom: dayjs(startDate).format("YYYY-MM-DD"),
        dateTo: dayjs(endDate).format("YYYY-MM-DD"),
      };
    }
    const to = dayjs();
    const from = to.subtract(daysToShow, "day");
    return {
      dateFrom: from.format("YYYY-MM-DD"),
      dateTo: to.format("YYYY-MM-DD"),
    };
  }, [daysToShow, startDate, endDate]);

  const { data } = useResourceGroupEmployeeUsageLineChartQuery({
    clientApplicationId,
    dateFrom,
    dateTo,
  });

  const chartData = useMemo(() => {
    if (!data?.resourceGroupUsage) {
      return { chartData: [], weekendDates: [] };
    }

    // Create a map of date to employee count
    const usageByDate = new Map<string, number>();
    data.resourceGroupUsage.forEach((usage) => {
      if (usage?.date && usage.employeeCount !== undefined) {
        usageByDate.set(
          dayjs(usage.date).format("YYYY-MM-DD"),
          usage.employeeCount,
        );
      }
    });

    // Generate data for all dates in our range
    const toDate = dayjs(dateTo);
    const actualDaysToShow = startDate && endDate 
      ? Math.max(1, toDate.diff(dayjs(dateFrom), "day") + 1)
      : daysToShow;
      
    const chartData = Array.from({ length: actualDaysToShow }, (_, i) => {
      const currentDate = toDate.subtract(actualDaysToShow - 1 - i, "day");
      const dateStr = currentDate.format("YYYY-MM-DD");
      const displayDate = currentDate.format("DD/MM/YYYY");
      const employeeCount = usageByDate.get(dateStr) || 0;

      return {
        x: displayDate,
        y: employeeCount,
      };
    });

    // Separately calculate weekend dates for highlighting
    const weekendDates = Array.from({ length: actualDaysToShow }, (_, i) => {
      const currentDate = toDate.subtract(actualDaysToShow - 1 - i, "day");
      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6; // Sunday = 0, Saturday = 6
      return isWeekend ? currentDate.format("DD/MM/YYYY") : null;
    }).filter(Boolean) as string[];

    return { chartData, weekendDates };
  }, [data, dateTo, dateFrom, daysToShow, startDate, endDate]);

  // Force chart re-render when date range changes to prevent artifacts
  const chartKey = useMemo(
    () => `${dateFrom}-${dateTo}-${clientApplicationId}`,
    [dateFrom, dateTo, clientApplicationId],
  );

  return (
    <LineChart
      key={chartKey}
      data={[
        {
          id: "user-usage",
          color: "red",
          data: chartData.chartData,
        },
      ]}
      weekendDates={chartData.weekendDates}
      xAxisName=""
      yAxisName="User count"
      tooltipLabel="Users"
    />
  );
};
